import '../Home.scss';
import { FC, ReactNode, useMemo, useState } from 'react';
import cn from 'classnames';
import Page from "@containers/Page/Page";
import PageContent from "@containers/PageContent/PageContent";
import { CustomBaseScrollbars } from "@components/CustomScrollbarsVirtualList/CustomScrollbarsVirtualList";
import TabList from '@components/new/TabList/TabList';
import { getSectionId } from '@helpers';
import HomeSection from "@pages/Home/components/HomeSection";
import { cssX, SearchOnKeyDownInit } from "@datassential/platform-ui-lib";
import { useHomeWorkspace } from '@pages/Home/context/HomeWorkspaceContext';

const HOME_NAVIGATION_OFFSET = 32;

export interface HomeWorkspaceSection {
    id: string;
    title: string;
    testId: string;
    Component: FC;
}

interface HomeWorkspaceLayoutProps {
    scrollbarId: string;
    sections: HomeWorkspaceSection[];
    navigationLeft?: ReactNode;
}

const HomeWorkspaceLayout: FC<HomeWorkspaceLayoutProps> = ({ scrollbarId, sections, navigationLeft }) => {
    const { visibleSections, isReady } = useHomeWorkspace();
    const [isScrolled, setIsScrolled] = useState(false);
    const tabListData = useMemo(
        () => sections
            .filter(({ id }) => visibleSections.has(id))
            .map(({ id, title }) => ({ id, title })),
        [sections, visibleSections]
    );
    const isLastSection = (index: number) => index === sections.length - 1;

    return (
        // @ts-ignore
        <Page>
            <PageContent>
                <SearchOnKeyDownInit/>
                <CustomBaseScrollbars
                    id={scrollbarId}
                    onScroll={(event: any) => {
                        setIsScrolled(event?.target?.scrollTop > 0);
                    }}
                >
                    <div className="Home">
                        <div
                            className={cn(
                                "Home__navigation",
                                "is-workspace",
                                cssX('-use-rem'),
                                isScrolled && "Home__navigation--scrolled",
                            )}
                        >
                            {navigationLeft}
                            {isReady && (
                                <TabList
                                    className={cn("Home__navigation-right", "appears")}
                                    data={tabListData}
                                    containerId={scrollbarId}
                                    offset={HOME_NAVIGATION_OFFSET}
                                />
                            )}
                        </div>
                        {sections.map(({ id, testId, Component }, index) => (
                            <HomeSection
                                key={id}
                                id={getSectionId(id)}
                                className={cn({ 'HomeSection--last': isLastSection(index) })}
                                testId={testId}
                            >
                                <Component/>
                            </HomeSection>
                        ))}
                    </div>
                </CustomBaseScrollbars>
            </PageContent>
        </Page>
    );
};

export default HomeWorkspaceLayout;
